import React from 'react'
import clsx from 'clsx'
import { CustomLink } from '@/components/atoms/CustomLink/CustomLink'

type MegaMenuItemProps = {
  item: {
    body?: string
    component: string
    icon?: ImageTypes
    title?: string
    link_url: Link
    _editable: string
    _uid: string
  }
  variant: 'products' | 'integrations' | 'features' | 'articles' | 'resources' | undefined
}

export function MegaMenuItem({ item, variant }: MegaMenuItemProps) {
  // TODO remove after updating production storyblok to new DS
  if (!variant) {
    return (
      <li
        className={clsx(
          'group flex flex-col mb-3 px-1 py-1 netbook:pt-0 netbook:px-0 netbook:mb-5 last:mb-0 w-full tablet:w-1/2 netbook:w-full',
          item?.icon?.image && 'w-full pb-5 border-b border-divider last:border-0 tablet:border-0 netbook:border-b'
        )}
      >
        <CustomLink {...item} new_tab={false} is_external={false} aria-label={item?.title}>
          <div className="flex items-center py-3">
            {item?.icon?.image && (
              <img src={item.icon.image} alt={item.title} className="mr-16" loading="lazy" decoding="async" />
            )}
            <p className={clsx('font-bold text-text-dark-900 group-hover:text-primary-500 text-bodyM')}>{item.title}</p>
          </div>
          <p className={clsx('text-bodyXS font-medium text-text-dark-600 mt-8')}>{item.body}</p>
        </CustomLink>
      </li>
    )
  }
  switch (variant) {
    case 'products': {
      return (
        <li className={clsx('group flex flex-col netbook:w-286 ')}>
          <CustomLink
            {...item}
            new_tab={false}
            is_external={false}
            aria-label={item.title}
            className="flex items-start gap-16"
          >
            {item?.icon?.image && (
              <img
                src={item.icon.image}
                alt={item.title}
                className="size-40 netbook:size-32"
                loading="lazy"
                decoding="async"
                width={40}
                height={40}
              />
            )}
            <div className="flex flex-col items-start gap-8">
              <p className={clsx('font-bold text-text-dark-900 group-hover:text-primary-500 text-bodyM')}>
                {item.title}
              </p>
              <p className={clsx('text-bodyXS font-medium text-text-dark-600')}>{item.body}</p>
            </div>
          </CustomLink>
        </li>
      )
    }
    case 'features': {
      return (
        <li className={clsx('group flex flex-col')}>
          <CustomLink {...item} new_tab={false} is_external={false} aria-label={item.title}>
            <p className={clsx('font-semibold text-primary-700 group-hover:text-primary-500 text-bodyM')}>
              {item.title}
            </p>
          </CustomLink>
        </li>
      )
    }
    case 'integrations': {
      return (
        <li className={clsx('hover:shadow-feature transition-shadow')}>
          <CustomLink
            {...item}
            new_tab={false}
            is_external={false}
            aria-label={item.title}
            className="grid place-items-center rounded p-10 border-1 border-grey-100 w-full netbook:w-140 h-72"
          >
            {item?.icon?.image && (
              <img
                src={item.icon.image}
                alt={item.title}
                className="min-h-14 h-full max-h-44 w-auto"
                loading="lazy"
                decoding="async"
                width={94}
                height={24}
              />
            )}
          </CustomLink>
        </li>
      )
    }
    case 'resources': {
      return (
        <li className={clsx('group flex flex-col netbook:w-250')}>
          <CustomLink
            {...item}
            new_tab={false}
            is_external={false}
            aria-label={item.title}
            className="flex items-start gap-16"
          >
            <div className="flex flex-col items-start gap-8">
              <p className={clsx('font-bold text-text-dark-900 group-hover:text-primary-500 text-bodyS')}>
                {item.title}
              </p>
              <p className={clsx('text-bodyXS font-medium text-text-dark-600')}>{item.body}</p>
            </div>
          </CustomLink>
        </li>
      )
    }
    case 'articles': {
      return (
        <li className={clsx('group flex flex-col w-full netbook:w-[366px] border-l-3 border-primary-500')}>
          <CustomLink
            {...item}
            new_tab={false}
            is_external={false}
            aria-label={item.title}
            className="flex items-start"
          >
            <div className="flex flex-col items-start gap-16 pl-16">
              <p className={clsx('font-semibold text-text-dark-900 group-hover:text-primary-500 text-bodyM')}>
                {item.title}
              </p>
              <p className={clsx('text-bodyS font-medium text-text-dark-600')}>{item.body}</p>
            </div>
          </CustomLink>
        </li>
      )
    }
    default:
      return null
  }
}
